import React, { useCallback, useMemo, useState } from 'react';
import { Platform } from 'interfaces/db';
import { 
    AutomationType, 
    TrellusDisposition, 
    TrellusDispositionToHumanReadable, 
    TrellusDispositionIsPrediction,
    AUTOMATION_TYPE_TO_HUMAN_READABLE
} from 'interfaces/settings';
import { Typography } from 'interfaces/typography';
import { useAutodialerSettings } from '../../hooks/useAutodialerSettings';
import { useAutodialerMappings } from '../../hooks/useAutodialerMappings';
import { Lock, Users, Building2, User } from 'lucide-react';
import { SelectionType } from "interfaces/settings";
import { useUserData } from '../../../../lib/redux/users/user-data-hook';
import { CleanSelect, CleanSelectOption } from 'utils/CommonUIComponents';
import { getCompactHierarchyColors } from '../../utils/hierarchyColors';

export const NONE_SELECTED = '___NO_MATCH__';

// Custom tooltip component
const Tooltip = ({ children, content, show }: { children: React.ReactNode, content: string, show: boolean }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    if (!show || !content) return <>{children}</>;
    
    return (
        <div 
            className="relative"
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
        >
            {children}
            {isVisible && (
                <div className="absolute z-10 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                    {content}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
            )}
        </div>
    );
};

export function LoggingMappingsControl() {
    const { selectedPlatform } = useAutodialerSettings();
    const { 
        getValueForMapping,
        getMappingIsEditable,
        getMappingSetByDifferentSelection,
        getMappingSetBySelection,
        getOptionsForSelection,
        updateMappingForSelection
    } = useAutodialerMappings();
    const { sortedUserGroupInfo: subgroups, allUsers } = useUserData();
    // Get automation types available for this platform
    const isSingletonAutomationType = useCallback((automationType: AutomationType): boolean => [AutomationType.PURPOSE, AutomationType.VM_INFO, AutomationType.CUSTOM].includes(automationType), []);
    const automationTypes = useMemo((): AutomationType[] => {
        const types = (() => {
            switch (selectedPlatform) {
                case Platform.SALESLOFT: 
                    return [AutomationType.DISPOSITION, AutomationType.SENTIMENT];
                case Platform.OUTREACH:
                    return [AutomationType.DISPOSITION, AutomationType.PURPOSE];
                case Platform.HUBSPOT:
                    return [AutomationType.DISPOSITION, AutomationType.PURPOSE];
                default: 
                    return [AutomationType.DISPOSITION];
            }
        })();
        
        return types;
    }, [selectedPlatform])

    // Determine if an automation type is singleton (uses SPECIAL_MATCH_ALL) or disposition-based
    const dispositionBasedTypes = automationTypes.filter(type => !isSingletonAutomationType(type));
    const singletonTypes = automationTypes.filter(type => isSingletonAutomationType(type));
    const dispositionTypes = Object.values(TrellusDisposition).filter(disposition => TrellusDispositionIsPrediction[disposition]);
    // Helper function to get display value for a mapping
    const getDisplayValue = (value: string | null | undefined): string => {
        if (!value) return 'Not set';
        if (value === NONE_SELECTED) return 'Do Not Map';
        return value;
    };

    // Helper function to get icon, label, and colors for who set the mapping
    const getIconLabelAndColors = useCallback((automationType: AutomationType, disposition: TrellusDisposition) => {
        const mappingSetBySelection = getMappingSetBySelection(automationType, disposition);
        if (!mappingSetBySelection) {
            return { 
                icon: <User size={12} />, 
                label: 'Set by other', 
                bgColor: 'bg-gray-100', 
                borderColor: 'border-gray-200', 
                textColor: 'text-gray-800' 
            };
        }

        const subgroup = mappingSetBySelection.type === SelectionType.SUB_TEAM 
            ? subgroups.find(g => g.group.user_group_id === mappingSetBySelection.id) 
            : undefined;
        const mappingUser = mappingSetBySelection.type === SelectionType.ME 
            ? allUsers?.find(u => u.user_id === mappingSetBySelection.id)
            : undefined;

        const { bgColor, borderColor, textColor } = getCompactHierarchyColors(mappingSetBySelection.type);
        
        switch (mappingSetBySelection.type) {
        case SelectionType.USER:
            return { 
                icon: <User size={12} />, 
                label: 'Set by user', 
                bgColor, 
                borderColor, 
                textColor 
            };
        case SelectionType.SUB_TEAM:
            return { 
                icon: <Users size={12} />, 
                label: subgroup ? `Set by subteam - ${subgroup.group.user_group_name}` : 'Set by subteam', 
                bgColor, 
                borderColor, 
                textColor 
            };
        case SelectionType.OVERALL_TEAM:
            return { 
                icon: <Building2 size={12} />, 
                label: 'Set by team', 
                bgColor, 
                borderColor, 
                textColor 
            };
        case SelectionType.ME:
            return { 
                icon: <User size={12} />, 
                label: mappingUser ? `Set by ${mappingUser.user_name}` : 'Set by user', 
                bgColor, 
                borderColor, 
                textColor 
            };
        default:
            return { 
                icon: <User size={12} />, 
                label: 'Set by other', 
                bgColor: 'bg-gray-100', 
                borderColor: 'border-gray-200', 
                textColor: 'text-gray-800' 
            };
        }
    }, [getMappingSetBySelection, subgroups, allUsers]);

    const renderMappingCell = useCallback((automationType: AutomationType, disposition: TrellusDisposition) => {
        const value = getValueForMapping(automationType, disposition);
        const isEditable = getMappingIsEditable(automationType, disposition);
        const isSetByDifferent = getMappingSetByDifferentSelection(automationType, disposition);
        const options = getOptionsForSelection(automationType);
        const isSet = value !== null && value !== undefined && value !== '';
        const handleValueChange = (newValue: string | number) => updateMappingForSelection(automationType, disposition, newValue.toString());
        
        const selectOptions: CleanSelectOption[] = [
            { label: 'Select mapping...', value: '' },
            { label: 'Do Not Map', value: NONE_SELECTED },
            ...options.map(option => ({ label: option, value: option }))
        ];

        const { icon: setByIcon, label: setByLabel, bgColor, borderColor, textColor } = isSetByDifferent ? getIconLabelAndColors(automationType, disposition) : { icon: null, label: '', bgColor: '', borderColor: '', textColor: '' };
        const shouldShowSetByLabel = isSetByDifferent && dispositionBasedTypes.length === 1;
        const tooltipText = isSetByDifferent ? setByLabel : '';

        if (!isEditable) {
            return (
                <Tooltip content={tooltipText} show={isSetByDifferent}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded text-xs min-w-0 flex-1">
                            <Lock size={10} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate text-gray-600">{getDisplayValue(value)}</span>
                        </div>
                </div>
                </Tooltip>
            )
        }

        if (isSetByDifferent) {
            return (
                <Tooltip content={tooltipText} show={true}>
                    <div className="flex items-center justify-between gap-1">
                        <div className={`${bgColor} px-1.5 py-1 rounded text-xs flex-1 min-w-0 border ${borderColor} flex items-center gap-1`}>
                            <span className={`${textColor} flex-shrink-0`}>{setByIcon}</span>
                            <span className={`${textColor} font-medium truncate`}>{getDisplayValue(value)}</span>
                            {shouldShowSetByLabel && <span className={`${textColor} text-xs opacity-75 ml-1`}>({setByLabel})</span>}
                        </div>
                        <button
                            onClick={() => updateMappingForSelection(automationType, disposition, value || '')}
                            className="px-1.5 py-0.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex-shrink-0"
                            title="Override this mapping"
                        >
                            Override
                        </button>
                </div>
                </Tooltip>
            );
        }

        // For editable mappings (both set and unset), always show the selector
        return (
            <div className="flex items-center justify-between gap-1">
                <div className="min-w-0 flex-1">
                    <CleanSelect
                        value={value || ''}
                        onChange={handleValueChange}
                        options={selectOptions}
                        placeholder="Select..."
                        className="w-full text-xs"
                    />
                </div>
                {isSet && (
                    <button
                        onClick={() => updateMappingForSelection(automationType, disposition, '')}
                        className="p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                        title="Clear this mapping"
                    >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                )}
            </div>
        );
    }, [getValueForMapping, getMappingIsEditable, getMappingSetByDifferentSelection, getOptionsForSelection, updateMappingForSelection, getIconLabelAndColors, dispositionBasedTypes]);
    if (!selectedPlatform) return null;

    return (
        <div className="w-full space-y-4">
            <div className="mb-3">
                <Typography variant="largeParagraph" fontWeight="600" color="#374151">
                    Automation Mappings
                </Typography>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                    <Typography variant="smallParagraph" color="#1e40af" className="leading-relaxed text-sm">
                        <strong>How it works:</strong> When Trellus makes one of these 6 predictions during a call, 
                        the corresponding platform value will be automatically filled in. If Trellus doesn't make a prediction, 
                        you'll be prompted to manually select the disposition.
                </Typography>
                </div>
            </div>
            
            {/* Disposition-based automation types */}
            {dispositionBasedTypes.length > 0 && (
            <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200 rounded-lg text-sm">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="border border-gray-200 p-1.5 text-left w-32">
                                    <Typography variant="smallParagraph" fontWeight="600" color="#374151" className="text-xs">
                                        Predicted
                                    </Typography>
                                </th>
                                {dispositionBasedTypes.map((automationType) => (
                                    <th key={automationType} className="border border-gray-200 p-1.5 text-left min-w-48">
                                        <Typography variant="smallParagraph" fontWeight="600" color="#374151" className="text-xs">
                                            {AUTOMATION_TYPE_TO_HUMAN_READABLE[automationType]}
                                    </Typography>
                                </th>
                            ))}
                        </tr>
                    </thead>
                        <tbody>
                            {dispositionTypes.map((disposition) => (
                                <tr key={disposition} className="hover:bg-gray-50">
                                    <td className="border border-gray-200 p-1.5">
                                        <Typography variant="smallParagraph" fontWeight="500" color="#374151" className="text-xs">
                                        {TrellusDispositionToHumanReadable[disposition]}
                                    </Typography>
                                    </td>
                                    {dispositionBasedTypes.map((automationType) => (
                                        <td key={`${disposition}-${automationType}`} className="border border-gray-200 p-1.5">
                                            {renderMappingCell(automationType, disposition)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}

            {/* Singleton automation types */}
            {singletonTypes.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200 rounded-lg text-sm">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="border border-gray-200 p-1.5 text-left w-32">
                                    <Typography variant="smallParagraph" fontWeight="600" color="#374151" className="text-xs">
                                        Setting
                                    </Typography>
                                </th>
                                <th className="border border-gray-200 p-1.5 text-left min-w-48">
                                    <Typography variant="smallParagraph" fontWeight="600" color="#374151" className="text-xs">
                                        Value
                                    </Typography>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {singletonTypes.map((automationType) => (
                                <tr key={automationType} className="hover:bg-gray-50">
                                    <td className="border border-gray-200 p-1.5">
                                        <Typography variant="smallParagraph" fontWeight="500" color="#374151" className="text-xs">
                                            {AUTOMATION_TYPE_TO_HUMAN_READABLE[automationType]}
                                        </Typography>
                                    </td>
                                    <td className="border border-gray-200 p-1.5">
                                        {renderMappingCell(automationType, TrellusDisposition.SPECIAL_MATCH_ALL)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
} 